import type { TFunction } from "i18next";

import type { Chase } from "./types";
import { chasePhase, topRung } from "./types";

import { registerCampaignType } from "../../registry";
import { canJoinCampaign } from "../../can-join";
import type { CampaignTypeModule, CurrentCampaign } from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectChaseById } from "@/stores/chase.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  CampaignDescriptionText,
  EligibleGamesSection,
  HowItWorksCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { CampaignFinishedCard } from "@/components/campaign-card/campaign-finished-card";
import { CampaignCompletedBanner } from "@/components/campaigns/campaign-completed-banner";
import { ChaseHomeCard } from "@/components/campaigns/chase/chase-home-card";
import { ChaseLadder } from "@/components/campaigns/chase/chase-ladder";
import { ChaseProgressCard } from "@/components/campaigns/chase/chase-progress-card";
import { ChaseTargetsCard } from "@/components/campaigns/chase/chase-targets-card";
import { FinishedChaseCard } from "@/components/campaign-card/finished-campaign/finished-chase-card";
import { AtLg, BelowLg } from "@/components/breakpoint";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";

const chaseData = (current: CurrentCampaign): Chase => current.data as Chase;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "chase") return null;
  const chase = chaseData(current);
  const campaign = chase.campaign;
  const phase = chasePhase(chase);
  const finished = phase === "finished";
  const joined = current.joined;
  const canJoin = canJoinCampaign(campaign, joined);
  const finishedTitle = t("chase.finished", "Chase finished");

  const howItWorks = (
    <HowItWorksCard
      text={t(
        "chase.howItWorks",
        "Hit every target in as few spins as you can. The fewer spins you need, the bigger the prize — and you can go again for the rest of the ladder."
      )}
      minimumBet={chase.minimum_bet}
    />
  );
  // Desktop keeps the ladder beside the run (sidebar); phones stack it under the targets.
  const ladder = <ChaseLadder chase={chase} />;

  return {
    campaign,
    primaryLabel: t("nav.overview", "Overview"),
    finished,
    mobileNotice: finished ? (
      <CampaignFinishedCard
        campaign={campaign}
        title={finishedTitle}
        className="ls:hidden"
      />
    ) : undefined,
    sidebar: finished ? (
      // The finished card takes the CTA slot; how-it-works and the ladder stay, greyed, and the
      // won rungs keep their gold inside it.
      <>
        <AtLg>
          <CampaignFinishedCard campaign={campaign} title={finishedTitle} />
        </AtLg>
        <AtLg>
          <div className={FINISHED_DIM}>{howItWorks}</div>
        </AtLg>
        <AtLg>{ladder}</AtLg>
      </>
    ) : (
      <>
        {canJoin && (
          <AtLg>
            <CampaignJoinCta campaign={campaign} />
          </AtLg>
        )}
        <AtLg>{howItWorks}</AtLg>
        <AtLg>{ladder}</AtLg>
      </>
    ),
    guide: finished ? (
      <div className={FINISHED_DIM}>{howItWorks}</div>
    ) : (
      howItWorks
    ),
    // The player's own run on top, the targets it is made of under it. The ladder joins them on
    // phones (desktop has it in the sidebar). Each card owns its over/finished treatment; the
    // targets dim as a block once the run is over.
    primary: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        {phase === "allWon" && (
          <CampaignCompletedBanner
            title={t("chase.allWonTitle", "Every prize claimed!")}
            subtitle={t(
              "chase.allWonSubtitle",
              "You've won every rung of this chase."
            )}
          />
        )}
        <ChaseProgressCard chase={chase} joined={joined} />
        <ChaseTargetsCard
          chase={chase}
          className={cn(finished && FINISHED_DIM)}
        />
        <BelowLg>{ladder}</BelowLg>
      </div>
    ),
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={campaign} />}
        >
          <CampaignDescriptionText text={campaign.campaign_info} />
        </InfoSection>
        <EligibleGamesSection
          games={chase.eligible_games}
          count={chase.eligible_games_count}
          campaignId={campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={campaign} />
    ) : undefined,
  };
}

/** The chase (`campaign.type "TO"` + `subtype "IM"`). */
const chaseModule: CampaignTypeModule = {
  id: "chase",
  resolve(state: AppStore, key): CurrentCampaign | null {
    const chase = selectChaseById(state, key);
    if (!chase) return null;
    return {
      type: "chase",
      campaign: chase.campaign,
      joined: selectJoined(state, key),
      data: chase,
    };
  },
  listActive: (state) =>
    state.chases
      .filter((chase) => isActiveCampaignStatus(chase.campaign.status))
      .map((chase) => ({ campaign: chase.campaign, data: chase })),
  listFinished: (state) =>
    state.chases
      .filter((chase) => isFinishedCampaignStatus(chase.campaign.status))
      .map((chase) => ({ campaign: chase.campaign, data: chase })),
  // The best prize is the cheapest rung — the ladder is sorted that way.
  hook: (entry, t) => {
    const top = topRung(entry.data as Chase);
    return top
      ? {
          label: t("campaign.topPrize", "Top prize"),
          value: formatPrizeTitle(top.prize.title, t),
          isCatalogItem: top.prize.is_catalog_item,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <ChaseHomeCard chase={entry.data as Chase} layout={layout} />
  ),
  finishedCard: (entry) => <FinishedChaseCard chase={entry.data as Chase} />,
  detailView,
  // The dashboard is this type's surface; the overlay shows its generic stand-in.
  dockPanel: () => null,
};

registerCampaignType(chaseModule);
